import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { loan_balance: "bal", current_salary: "s", salary_growth: "growth", voluntary_payment: "vol", overseas: "overseas" }
const URL_TYPES = { loan_balance: "number", current_salary: "number", voluntary_payment: "number", overseas: "boolean" }

export default class extends Controller {
  static targets = [
    "balanceInput", "salaryInput", "salaryGrowthInput", "overseasCheckbox",
    "voluntaryInput", "voluntaryRow", "voluntaryAmount",
    "resultsPanel", "loadingIndicator", "introContent",
    "payoffDate", "payoffYears", "totalRepaid", "totalInterest",
    "annualRepayment", "fortnightlyRepayment", "fortnightlyRow",
    "repaymentNote", "repaymentNoteOverseas",
    "chartCanvas", "breakdownHead", "breakdownBody",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._chart = null
    this._prefillFromUrl()
    this._renderPrerenderedExtras()
  }

  disconnect() {
    this._debounce.cancel()
    if (this._chart) this._chart.destroy()
  }

  inputChanged(event) { this._debounce.schedule(detectEventType(event), this.salaryInputTarget.value) }
  optionChanged() { this._debounce.schedule("toggle", this.salaryInputTarget.value) }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  async _performCalculation() {
    const balance = parseNumber(this.balanceInputTarget.value)
    const salary = parseNumber(this.salaryInputTarget.value)
    if (!balance || balance <= 0 || !salary || salary <= 0) return

    this._showLoading()

    try {
      const params = {
        type: "student_loan",
        loan_balance: balance,
        current_salary: salary,
        gross_salary: salary,
        overseas: this.hasOverseasCheckboxTarget ? this.overseasCheckboxTarget.checked : false
      }

      if (this.hasSalaryGrowthInputTarget) {
        params.salary_growth = this.salaryGrowthInputTarget.value
      }

      if (this.hasVoluntaryInputTarget) {
        const vol = parseNumber(this.voluntaryInputTarget.value)
        if (vol > 0) params.voluntary_payment = vol
      }

      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("student_loan", { salaryAmount: salary })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    const unpaid = data.payoff_date && data.payoff_date.startsWith("Not within")
    this.payoffDateTarget.textContent = data.payoff_date
    this.payoffYearsTarget.textContent = unpaid
      ? `${data.payoff_years}+ years`
      : `${data.payoff_years} year${data.payoff_years !== 1 ? "s" : ""}`
    this.totalRepaidTarget.textContent = formatCurrency(data.total_repaid)
    this.annualRepaymentTarget.textContent = formatCurrency(data.annual_repayment)
    this.fortnightlyRepaymentTarget.textContent = formatCurrency(data.fortnightly_repayment)

    if (this.hasTotalInterestTarget) {
      this.totalInterestTarget.textContent = formatCurrency(data.total_interest || 0)
      this.totalInterestTarget.closest("[data-interest-row]").classList.toggle(
        "hidden", (data.total_interest || 0) <= 0
      )
    }

    if (this.hasVoluntaryRowTarget) {
      const vol = data.voluntary_weekly || 0
      if (vol > 0) {
        this.voluntaryAmountTarget.textContent = `${formatCurrency(vol)}/week`
        this.voluntaryRowTarget.classList.remove("hidden")
      } else {
        this.voluntaryRowTarget.classList.add("hidden")
      }
    }

    const hasVoluntary = (data.voluntary_weekly || 0) > 0
    const isOverseas = data.overseas_toggle || data.overseas || false
    this._updateOverseasContext(isOverseas)
    this._renderChart(data.yearly_breakdown)
    this._renderBreakdown(data.yearly_breakdown, hasVoluntary)
    this._updateBreakdownHeader(hasVoluntary)
    this._showResults()

    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = unpaid
        ? `Student loan not paid off within ${data.payoff_years} years. Total repaid so far: ${formatCurrency(data.total_repaid)}.`
        : `Student loan paid off in ${data.payoff_years} years (${data.payoff_date}). Total repaid: ${formatCurrency(data.total_repaid)}.`
    }
  }

  _updateOverseasContext(isOverseas) {
    if (this.hasFortnightlyRowTarget) {
      this.fortnightlyRowTarget.classList.toggle("hidden", isOverseas)
    }
    if (this.hasRepaymentNoteTarget) {
      this.repaymentNoteTarget.classList.toggle("hidden", isOverseas)
    }
    if (this.hasRepaymentNoteOverseasTarget) {
      this.repaymentNoteOverseasTarget.classList.toggle("hidden", !isOverseas)
    }
  }

  _renderBreakdown(breakdown, hasVoluntary) {
    if (!this.hasBreakdownBodyTarget || !breakdown) return

    this.breakdownBodyTarget.innerHTML = breakdown.map(row => {
      const volCell = hasVoluntary
        ? `<td class="px-4 py-1.5 text-sm text-right tabular-nums text-green-600">${formatCurrency(row.voluntary || 0)}</td>`
        : ""
      return `
      <tr class="border-b border-gray-100 last:border-0">
        <td class="pl-6 pr-4 py-1.5 text-sm text-gray-700 tabular-nums">${row.year}</td>
        <td class="px-4 py-1.5 text-sm text-right tabular-nums text-gray-900">${formatCurrency(row.salary)}</td>
        <td class="px-4 py-1.5 text-sm text-right tabular-nums text-green-600">${formatCurrency(row.repayment)}</td>
        ${volCell}
        <td class="pl-4 pr-6 py-1.5 text-sm text-right tabular-nums font-medium text-gray-900">${formatCurrency(row.balance)}</td>
      </tr>`
    }).join("")
  }

  _updateBreakdownHeader(hasVoluntary) {
    if (!this.hasBreakdownHeadTarget) return
    const volHeader = hasVoluntary
      ? `<th class="px-4 py-2 text-right font-medium">Voluntary</th>`
      : ""
    this.breakdownHeadTarget.innerHTML = `
      <th class="pl-6 pr-4 py-2 text-left font-medium">Year</th>
      <th class="px-4 py-2 text-right font-medium">Salary</th>
      <th class="px-4 py-2 text-right font-medium">Repayment</th>
      ${volHeader}
      <th class="pl-4 pr-6 py-2 text-right font-medium">Balance</th>`
  }

  async _renderChart(breakdown) {
    if (!this.hasChartCanvasTarget || !breakdown || breakdown.length === 0) return

    try {
      const { Chart, BarElement, LinearScale, CategoryScale, Tooltip } = await import("chart.js")
      Chart.register(BarElement, LinearScale, CategoryScale, Tooltip)

      if (this._chart) this._chart.destroy()

      this._chart = new Chart(this.chartCanvasTarget, {
        type: "bar",
        data: {
          labels: breakdown.map(r => `Year ${r.year}`),
          datasets: [{
            label: "Remaining Balance",
            data: breakdown.map(r => r.balance),
            backgroundColor: breakdown.map(r => r.balance > 0 ? "#0E7490" : "#059669"),
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          plugins: {
            tooltip: {
              callbacks: {
                label: (ctx) => `Balance: $${ctx.parsed.y.toLocaleString()}`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` }
            }
          }
        }
      })
    } catch {
      // Chart.js not available
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.loan_balance || !p.current_salary) return
    this.balanceInputTarget.value = p.loan_balance
    this.salaryInputTarget.value = p.current_salary
    if (p.salary_growth != null && this.hasSalaryGrowthInputTarget) this.salaryGrowthInputTarget.value = p.salary_growth
    if (p.voluntary_payment != null && this.hasVoluntaryInputTarget) this.voluntaryInputTarget.value = p.voluntary_payment
    if (p.overseas && this.hasOverseasCheckboxTarget) this.overseasCheckboxTarget.checked = true
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      if (data.yearly_breakdown) {
        const hasVoluntary = (data.voluntary_weekly || 0) > 0
        requestAnimationFrame(() => {
          this._renderChart(data.yearly_breakdown)
          this._renderBreakdown(data.yearly_breakdown, hasVoluntary)
          this._updateBreakdownHeader(hasVoluntary)
        })
      }
    } catch { /* ignore parse errors */ }
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
